import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { beobachteSitzung, meldeAktivenNutzer } from '@/lib/sitzungskanal';

/**
 * Hält alle Fenster darüber im Bild, wer gerade angemeldet ist (B88).
 *
 * Drei Aufgaben:
 *  1. Den eigenen Stand bekanntgeben, sobald er feststeht oder sich ändert
 *     — das deckt Anmeldung, Nutzerwechsel und Abmeldung ab, ohne dass
 *     jede dieser Stellen daran denken muss.
 *  2. Auf fremde Meldungen hören und alles neu laden, wenn dort ein
 *     anderer Nutzer aktiv ist als hier angezeigt.
 *  3. Beim Zurückkehren ins Fenster nachfragen — für den Fall, dass eine
 *     Meldung nicht ankam (anderes Gerät, geschlossenes Fenster,
 *     schlafender Tab).
 *
 * Neu geladen wird großzügig: nicht nur die Anmeldung, sondern alle
 * Abfragen. Sichtbare Daten hängen am Nutzer — „meine Bestellungen“,
 * Erfasser-Kürzel, Bearbeitungsrechte auf Kommunikationseinträgen.
 */
export function useSitzungsSync(): void {
  const qc = useQueryClient();
  const auth = useAuth();
  const eigener = auth.data?.user.nc_username ?? null;

  // undefined = in diesem Fenster wurde noch nichts gemeldet.
  const gemeldet = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (auth.data === undefined) return;
    if (gemeldet.current === eigener) return;
    gemeldet.current = eigener;
    meldeAktivenNutzer(eigener);
  }, [auth.data, eigener]);

  useEffect(
    () =>
      beobachteSitzung((aktiv) => {
        // Schon aktuell — passiert regelmäßig, weil Kanal und
        // storage-Ereignis dieselbe Meldung zustellen.
        if (aktiv === eigener) return;
        gemeldet.current = undefined;
        void qc.invalidateQueries();
      }),
    [qc, eigener]
  );

  useEffect(() => {
    const nachfragen = (): void => {
      if (document.visibilityState === 'hidden') return;
      void qc.invalidateQueries({ queryKey: ['auth'] });
    };
    window.addEventListener('focus', nachfragen);
    document.addEventListener('visibilitychange', nachfragen);
    return () => {
      window.removeEventListener('focus', nachfragen);
      document.removeEventListener('visibilitychange', nachfragen);
    };
  }, [qc]);
}
